/**
 * ProductTagsBadges Component
 *
 * Displays product tags as clickable badges.
 * Used in basket/portfolio detail pages to show tag information.
 *
 * Props:
 * - tags: ProductTagsData object with tag values
 * - onClick: Callback when any badge is clicked (opens info modal)
 * - compact: If true, shows abbreviated display for list views
 */

import { Award, DollarSign, Target, TrendingUp } from 'lucide-react';
import React from 'react';
import {
    formatCurrencyTagCompact,
    formatRiskScore,
    getRiskLevelClassString,
    getTagClasses,
} from '../config/productTagsConfig';
import { useTheme } from '../contexts/ThemeContext';
import type { ProductTagsData } from '../types/productTags';
import { getRiskLevel, hasAnyTags } from '../types/productTags';

interface ProductTagsBadgesProps {
  /** Tag data to display */
  tags: ProductTagsData | undefined | null;
  /** Callback when any badge is clicked */
  onClick?: () => void;
  /** Compact mode for list views */
  compact?: boolean;
  /** Hide the featured badge (when shown elsewhere) */
  hideFeatured?: boolean;
  /** Additional CSS classes */
  className?: string;
  appearance?: "default" | "alpha";
}

/**
 * Single badge component with consistent styling
 */
interface BadgeProps {
  children: React.ReactNode;
  colorClasses: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  appearance?: "default" | "alpha";
}

const Badge: React.FC<BadgeProps> = ({ children, colorClasses, onClick, icon, appearance = "default" }) => {
  const baseClasses = appearance === "alpha"
    ? "inline-flex cursor-pointer items-center gap-1 rounded-v7-control border px-2.5 py-1 text-xs font-medium transition-colors hover:border-v7-bronze-dark"
    : "inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-all hover:scale-105 hover:shadow-md";

  return (
    <span
      onClick={onClick}
      className={`${baseClasses} ${colorClasses}`.trim().replace(/\s+/g, ' ')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const ProductTagsBadges: React.FC<ProductTagsBadgesProps> = ({
  tags,
  onClick,
  compact = false,
  hideFeatured = false,
  className = '',
  appearance = "default",
}) => {
  const { isDarkMode } = useTheme();
  const useDarkTagColors = appearance === "alpha" ? false : isDarkMode;

  // Don't render if no tags
  if (!hasAnyTags(tags)) {
    return null;
  }

  const handleClick = () => {
    onClick?.();
  };

  // Get risk level colors (with dark mode support)
  const riskLevel = getRiskLevel(tags?.risk);
  const riskClasses = getRiskLevelClassString(riskLevel, useDarkTagColors);

  // Render badges
  const badges: React.ReactNode[] = [];

  // Risk Score badge
  if (tags?.risk !== undefined) {
    badges.push(
      <Badge
        key="risk"
        colorClasses={riskClasses}
        onClick={handleClick}
        icon={<TrendingUp className="w-3 h-3" />}
        appearance={appearance}
      >
        Risk: {formatRiskScore(tags.risk)}
      </Badge>
    );
  }

  // Risk Type badge (show alongside Risk Score per documentation)
  if (tags?.riskType !== undefined) {
    const riskTypeLevel = getRiskLevel(
      tags.riskType === 'Extremely High' || tags.riskType === 'High'
        ? 8
        : tags.riskType === 'Medium'
          ? 5
          : 2
    );
    badges.push(
      <Badge
        key="riskType"
        colorClasses={getRiskLevelClassString(riskTypeLevel, useDarkTagColors)}
        onClick={handleClick}
        icon={<TrendingUp className="w-3 h-3" />}
        appearance={appearance}
      >
        {tags.riskType}
      </Badge>
    );
  }

  // Investment Focus badge
  if (tags?.focus !== undefined) {
    const focusColors = getTagClasses('focus', useDarkTagColors);
    badges.push(
      <Badge
        key="focus"
        colorClasses={`${focusColors.bg} ${focusColors.text} ${focusColors.border}`}
        onClick={handleClick}
        icon={<Target className="w-3 h-3" />}
        appearance={appearance}
      >
        {compact ? tags.focus.split(' ')[0] : tags.focus}
      </Badge>
    );
  }

  // Min Investment badge
  if (tags?.minInvestment !== undefined) {
    const minInvColors = getTagClasses('minInvestment', useDarkTagColors);
    badges.push(
      <Badge
        key="minInvestment"
        colorClasses={`${minInvColors.bg} ${minInvColors.text} ${minInvColors.border}`}
        onClick={handleClick}
        icon={<DollarSign className="w-3 h-3" />}
        appearance={appearance}
      >
        Min: {formatCurrencyTagCompact(tags.minInvestment)}
      </Badge>
    );
  }

  // Featured badge (can be hidden when displayed elsewhere, e.g., in header)
  if (tags?.featured === true && !hideFeatured) {
    const featuredColors = getTagClasses('featured', useDarkTagColors);
    badges.push(
      <Badge
        key="featured"
        colorClasses={`${featuredColors.bg} ${featuredColors.text} ${featuredColors.border}`}
        onClick={handleClick}
        icon={<Award className="w-3 h-3" />}
        appearance={appearance}
      >
        Featured
      </Badge>
    );
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`.trim()}
      data-testid="product-tags-badges"
    >
      {badges}
    </div>
  );
};

export default ProductTagsBadges;
